/**
 * TopPrioritiesCard - Phase 3.1 Dashboard Enhancement
 * 
 * Shows the top 3 highest-impact pending items that need attention.
 * Each item shows expected claim impact and provides one-click navigation.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import { FinancialValue } from '../shared/FinancialValue';
import {
  AlertTriangle,
  DollarSign,
  FileText,
  ArrowRight,
  Clock,
  Loader2,
  Target,
  TrendingUp,
  CheckCircle
} from 'lucide-react';

interface PriorityItem {
  id: string;
  title: string;
  description: string;
  type: 'missing_data' | 'needs_review' | 'classification' | 'calculation';
  impact: number | null; // Dollar impact on claim
  urgency: 'critical' | 'high' | 'medium';
  route: string;
  draftId?: string;
  reportId?: string;
}

interface TopPrioritiesCardProps {
  maxItems?: number;
  className?: string;
  reportId?: string; // If provided, show priorities for this specific case
}

const URGENCY_STYLES = {
  critical: {
    surface: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: { color: 'var(--color-brand)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
  },
  high: {
    surface: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: { color: 'var(--color-brand)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
  },
  medium: {
    surface: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: { color: 'var(--color-brand)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
  }
};

const TYPE_ICONS = {
  missing_data: FileText,
  needs_review: AlertTriangle,
  classification: Target,
  calculation: TrendingUp
};

export default function TopPrioritiesCard({ maxItems = 3, className = '', reportId }: TopPrioritiesCardProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [priorities, setPriorities] = useState<PriorityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPriorities();
  }, [reportId]);

  const fetchPriorities = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      
      // If reportId provided, fetch case-specific priorities
      const endpoint = reportId 
        ? `/api/v1/queue/case/${reportId}/priorities`
        : '/api/v1/dashboard/top-priorities';
      
      const data = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setPriorities((data.priorities || []).slice(0, maxItems));
    } catch (err) {
      console.debug('Top priorities API not available');
      setPriorities([]);
      setError('unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item: PriorityItem) => {
    navigate(item.route);
  };

  if (loading) {
    return (
      <div className={`rounded-xl shadow-sm border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Top Priorities</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        </div>
      </div>
    );
  }

  if (priorities.length === 0) {
    return (
      <div className={`rounded-xl shadow-sm border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Top Priorities</h3>
        </div>
        {error ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertTriangle className="w-8 h-8 mb-2" style={{ color: 'var(--color-text-dim)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Priorities unavailable</p>
            <button
              onClick={fetchPriorities}
              className="mt-2 text-xs"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="w-12 h-12 mb-3" style={{ color: 'var(--color-verified)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>All caught up!</p>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No high-priority items need attention</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-sm border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Top Priorities</h3>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {priorities.length} {priorities.length === 1 ? 'item needs' : 'items need'} attention
        </span>
      </div>

      <div className="space-y-3">
        {priorities.map((item, index) => {
          const styles = URGENCY_STYLES[item.urgency];
          const Icon = TYPE_ICONS[item.type];

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="w-full text-left p-4 rounded-lg border hover:shadow-md transition-all group"
              style={styles.surface}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-surface)', ...styles.icon }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                        {item.title}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={styles.badge}>
                        {item.urgency}
                      </span>
                    </div>
                    <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {item.description}
                    </p>
                    {item.impact != null && item.impact > 0 && (
                      <div className="flex items-center mt-2 text-sm">
                        <DollarSign className="w-4 h-4 mr-1" style={{ color: 'var(--color-verified)' }} />
                        <span className="font-medium" style={{ color: 'var(--color-verified)' }}>
                          <FinancialValue value={item.impact} /> potential impact
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-all flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
